import { Box, Typography, useTheme, Menu, MenuItem } from "@mui/material";
import React, { useEffect, useState } from "react";
import Assets from "../../Component/Common/Assets";
import {
  Chart as ChartJS,
  CategoryScale,
  LinearScale,
  PointElement,
  LineElement,
  Title,
  Tooltip,
  Filler,
  Legend,
} from "chart.js";
import { Line } from "react-chartjs-2";
import usePageLoader from "../../Redux/hooks/usePageLoader";
import { useDispatch } from "react-redux";
import useSnackBar from "../../Redux/hooks/useSnackBar";
import { getExpenseAction } from "../../Redux/Actions/DashboardActions";
import ErrorHandler from "../../lib/errorHandler";
import dayjs from "dayjs";

ChartJS.register(
  CategoryScale,
  LinearScale,
  PointElement,
  LineElement,
  Title,
  Tooltip,
  Filler,
  Legend
);

const months = [
  "Jan",
  "Feb",
  "Mar",
  "Apr",
  "May",
  "Jun",
  "Jul",
  "Aug",
  "Sep",
  "Oct",
  "Nov",
  "Dec",
];

const monthIndices = {
  January: 0,
  February: 1,
  March: 2,
  April: 3,
  May: 4,
  June: 5,
  July: 6,
  August: 7,
  September: 8,
  October: 9,
  November: 10,
  December: 11,
};

const Expense = () => {
  const theme = useTheme();
  const setFullPageLoader = usePageLoader();
  const dispatch = useDispatch();
  const { setSnackBar } = useSnackBar();

  // --------------------- State ---------------------
  const [yearMenuAnchor, setYearMenuAnchor] = useState(null);
  const [expenseData, setExpenseData] = useState([]);
  const [chartDataValues, setChartDataValues] = useState(new Array(12).fill(0));
  const [valueScale, setValueScale] = useState(() =>
    getValueScale(chartDataValues)
  );

  const currentDate = dayjs();
  const financialYears = generateFinancialYears(10);
  const [selectedFinancialYear, setSelectedFinancialYear] = useState(
    financialYears[0]
  );

  // --------------------- Helpers ---------------------
  function getValueScale(values) {
    const maxValue = Math.max(...values.map((val) => val || 0));

    if (maxValue >= 10000000)
      return { divisor: 10000000, suffix: "Cr", stepSize: 5000000 };
    if (maxValue >= 100000)
      return { divisor: 100000, suffix: "L", stepSize: 100000 };
    if (maxValue >= 1000) return { divisor: 1000, suffix: "K", stepSize: 1000 };
    return { divisor: 1, suffix: "", stepSize: 100 };
  }

  function generateFinancialYears(count) {
    const currentYear = currentDate.year();
    const currentMonth = currentDate.month();
    const financialYearStartMonth = 3; // April
    const currentFinancialYearStart =
      currentMonth >= financialYearStartMonth ? currentYear : currentYear - 1;

    return Array.from({ length: count }, (_, i) => {
      const startYear = currentFinancialYearStart - i;
      return `${startYear}-${(startYear + 1).toString().slice(-2)}`;
    });
  }

  function getFinancialYearRange() {
    const startYear = parseInt(selectedFinancialYear.split("-")[0]);
    const endYear = startYear + 1;

    const startDate = dayjs()
      .year(startYear)
      .month(3)
      .date(1)
      .format("YYYY-MM-DD"); // Apr 1
    const endDate = dayjs()
      .year(endYear)
      .month(2)
      .date(31)
      .format("YYYY-MM-DD"); // Mar 31

    return { startDate, endDate, year: startYear };
  }

  function calculateYAxisMax() {
    const maxValue = Math.max(...chartDataValues);
    if (maxValue === 0) return 100;

    const paddedMax = maxValue * 1.2;
    if (paddedMax >= 10000000) return Math.ceil(paddedMax / 5000000) * 5000000;
    if (paddedMax >= 100000) return Math.ceil(paddedMax / 100000) * 100000;
    if (paddedMax >= 1000) return Math.ceil(paddedMax / 1000) * 1000;
    return Math.ceil(paddedMax / 100) * 100;
  }

  // --------------------- Handlers ---------------------
  const handleYearClick = (event) => {
    setYearMenuAnchor(event.currentTarget);
  };

  const handleFinancialYearSelect = (financialYear) => {
    setSelectedFinancialYear(financialYear);
    setYearMenuAnchor(null);
  };

  const getExpenseData = async () => {
    setFullPageLoader(true);
    const { startDate, endDate } = getFinancialYearRange();
    const payload = { startDate, endDate };

    try {
      const res = await dispatch(getExpenseAction(payload));
      const ok = ErrorHandler(res, setSnackBar);

      if (ok && res.payload?.data?.response) {
        setExpenseData(res.payload.data.response);
      }
    } catch (err) {
      setSnackBar("error", err.message);
    } finally {
      setFullPageLoader(false);
    }
  };
  // --------------------- Effects ---------------------
  useEffect(() => {
    getExpenseData();
  }, [selectedFinancialYear]);

  useEffect(() => {
    setValueScale(getValueScale(chartDataValues));
  }, [chartDataValues]);

  useEffect(() => {
    if (Array.isArray(expenseData) && expenseData.length > 0) {
      const newChartData = new Array(12).fill(0);
      expenseData.forEach((item) => {
        const monthIndex = monthIndices[item.month];
        if (monthIndex !== undefined)
          newChartData[monthIndex] = item.total || 0;
      });
      setChartDataValues(newChartData);
    }
  }, [expenseData]);

  // --------------------- Chart Config ---------------------
  const chartData = {
    labels: months,
    datasets: [
      {
        label: "Expense",
        data: chartDataValues,
        fill: true,
        backgroundColor: (context) => {
          const ctx = context.chart.ctx;
          const gradient = ctx.createLinearGradient(0, 0, 0, 300);
          gradient.addColorStop(0, "rgba(255, 82, 0, 0.65)");
          gradient.addColorStop(0.4, "rgba(255, 82, 0, 0.3)");
          gradient.addColorStop(0.7, "rgba(255, 255, 255, 0.3)");
          gradient.addColorStop(1, "rgba(255, 255, 255, 0.1)");
          return gradient;
        },
        borderColor: "#FDA181",
        borderWidth: 3,
        pointRadius: 0,
        pointHoverRadius: 6,
        pointHoverBorderWidth: 2,
        pointHoverBorderColor: "#FDA181",
        pointHoverBackgroundColor: theme.palette.bgWhite.secondary,
        tension: 0.4,
      },
    ],
  };

  const options = {
    responsive: true,
    maintainAspectRatio: false,
    plugins: {
      legend: { display: false },
      tooltip: {
        backgroundColor: "rgba(0, 0, 0, 0.8)",
        titleColor: theme.palette.bgWhite.secondary,
        bodyColor: theme.palette.bgWhite.secondary,
        cornerRadius: 8,
        displayColors: false,
        callbacks: {
          label: (context) => {
            const value = context.parsed.y;
            if (value >= 10000000)
              return `₹${(value / 10000000).toFixed(2)} Cr`;
            if (value >= 100000) return `₹${(value / 100000).toFixed(2)} L`;
            if (value >= 1000) return `₹${(value / 1000).toFixed(2)} K`;
            return `₹${value.toLocaleString()}`;
          },
        },
      },
    },
    scales: {
      x: {
        grid: { display: false },
        border: { display: false },
        ticks: {
          color: theme.palette.bgBlack.secondary,
          font: { size: 12 },
          padding: 10,
        },
      },
      y: {
        beginAtZero: true,
        max: calculateYAxisMax(),
        grid: { display: false },
        border: { display: false },
        ticks: {
          display: true,
          color: theme.palette.bgBlack.secondary,
          font: { size: 12 },
          callback: (value) =>
            `${(value / valueScale.divisor).toFixed(1)}${valueScale.suffix}`,
          padding: 10,
        },
      },
    },
    interaction: { intersect: false, mode: "index" },
    elements: { point: { hoverRadius: 8 } },
  };

  // --------------------- Render ---------------------
  return (
    <Box
      sx={{
        width: "100%",
        height: "352px",
        background: theme.palette.bgWhite.third,
        borderRadius: "20px",
        border: "1px solid",
        borderColor: theme.palette.bgWhite.secondary,
        padding: "20px 20px",
        boxSizing: "border-box",
      }}
    >
      {/* Header */}
      <Box
        sx={{
          display: "flex",
          justifyContent: "space-between",
          flexWrap: "wrap",
        }}
      >
        <Typography
          color={theme.palette.bgBlack.secondary}
          fontWeight={700}
          fontSize="18px"
        >
          Expense
        </Typography>

        {/* Year Dropdown */}
        <Box
          sx={{
            display: "flex",
            alignItems: "center",
            gap: 1,
            flexWrap: "wrap",
          }}
        >
          <Box
            onClick={handleYearClick}
            sx={{
              display: "flex",
              padding: "6px 16px",
              alignItems: "center",
              gap: "8px",
              borderRadius: "30px",
              border: `1px solid ${theme.palette.bgWhite.secondary}`,
              boxShadow:
                "46px 32px 16px 0 rgba(183, 194, 215, 0.00), 29px 21px 14px 0 rgba(183, 194, 215, 0.01), 16px 12px 12px 0 rgba(183, 194, 215, 0.05), 7px 5px 9px 0 rgba(183, 194, 215, 0.09), 2px 1px 5px 0 rgba(183, 194, 215, 0.10)",
              cursor: "pointer",
            }}
          >
            <Assets absolutePath src="/assets/img/Calendar.svg" />
            <Typography
              fontSize="12px"
              fontWeight={700}
              color={theme.palette.bgDarkGray.grey600}
            >
              FY {selectedFinancialYear}
            </Typography>
          </Box>

          <Menu
            anchorEl={yearMenuAnchor}
            open={Boolean(yearMenuAnchor)}
            onClose={() => setYearMenuAnchor(null)}
          >
            {financialYears.map((year) => (
              <MenuItem
                key={year}
                onClick={() => handleFinancialYearSelect(year)}
              >
                FY {year}
              </MenuItem>
            ))}
          </Menu>
        </Box>
      </Box>

      {/* Chart */}
      <Box sx={{ width: "100%", height: "260px", mt: 2 }}>
        <Line data={chartData} options={options} />
      </Box>
    </Box>
  );
};

export default Expense;
